import { Memo, observer, use$ } from '@legendapp/state/react';
import {
  Card,
  type CardProps,
  Divider,
  NumberFormatter,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  AutoBetOnWinLossAction,
  autoBetState$,
  configurationState$,
} from '../../../state';

// Simple label-value pair component
const LabelValue = ({
  label,
  children,
  dataTestId,
}: {
  label: string;
  children: React.ReactNode;
  dataTestId: string;
}) => (
  <Stack data-test-id={`${dataTestId}-container`} gap={6}>
    <Text data-test-id={`${dataTestId}-label`} size="sm">
      {label}
    </Text>
    <Text data-test-id={`${dataTestId}-value`} fw={500} size="sm">
      {children}
    </Text>
  </Stack>
);

const getFormattedValue = (value: number) => {
  if (value === 0) {
    return '0%';
  }

  return value > 0 ? `+${value}%` : `${value}%`;
};

// Component for the Win/Loss action display with color coding
const ActionValue = ({
  action,
  value,
  dataTestId,
}: {
  action: AutoBetOnWinLossAction;
  value: number;
  dataTestId: string;
}) => {
  let color = 'white';

  if (action === AutoBetOnWinLossAction.RESET) {
    return <span data-test-id={`${dataTestId}-reset`}>-</span>;
  }

  // Determine color based on conditions
  if (action === AutoBetOnWinLossAction.INCREASE_OR_DECREASE && value === 0) {
    color = 'orange';
  } else if (value < 0) {
    color = 'red';
  } else if (value > 0) {
    color = 'green';
  }

  // Format the value with the appropriate sign
  const formattedValue = getFormattedValue(value);

  return (
    <Text
      c={color}
      component="span"
      data-test-id={`${dataTestId}-action`}
      fw={500}
    >
      {formattedValue}
    </Text>
  );
};

// Component for displaying currency values with formatting
const CurrencyValue = observer(
  ({
    value,
    color,
    prefix,
  }: {
    value: number;
    color: string;
    prefix: string;
  }) => {
    // Apply appropriate color based on value
    const defaultColor = 'white';
    const defaultPrefix = '';

    return (
      <Text
        c={value === 0 ? defaultColor : color}
        component="span"
        data-test-id={`currency-value-${color}`}
      >
        {value === 0 ? defaultPrefix : prefix}
        <NumberFormatter
          data-test-id="currency-formatter"
          decimalScale={2}
          fixedDecimalScale
          value={Math.abs(value)}
        />{' '}
        <Memo>{configurationState$.currency}</Memo>
      </Text>
    );
  }
);

// Win/Loss section component
const WinLossSection = observer(() => {
  const { onWinIncreaseValue, onLossIncreaseValue, onWinAction, onLossAction } =
    use$({
      onWinIncreaseValue: autoBetState$.onWinIncreaseValue.get(),
      onLossIncreaseValue: autoBetState$.onLossIncreaseValue.get(),
      onWinAction: autoBetState$.onWinAction.get(),
      onLossAction: autoBetState$.onLossAction.get(),
    });

  const { t } = useTranslation();

  return (
    <SimpleGrid cols={2} data-test-id="win-loss-section">
      <LabelValue dataTestId="on-win" label={t('autobet.onWinLabel')}>
        <ActionValue
          action={onWinAction}
          dataTestId="on-win"
          value={onWinIncreaseValue}
        />
      </LabelValue>
      <LabelValue dataTestId="on-loss" label={t('autobet.onLossLabel')}>
        <ActionValue
          action={onLossAction}
          dataTestId="on-loss"
          value={onLossIncreaseValue}
        />
      </LabelValue>
    </SimpleGrid>
  );
});

// Stop conditions section component
const StopConditionsSection = observer(() => {
  const { stopOnProfitValue, stopOnLossValue } = use$({
    stopOnProfitValue: autoBetState$.stopOnProfitValue.get(),
    stopOnLossValue: autoBetState$.stopOnLossValue.get(),
  });

  const { t } = useTranslation();

  return (
    <SimpleGrid cols={2} data-test-id="stop-conditions-section">
      <LabelValue
        dataTestId="stop-on-profit"
        label={t('autobet.stopOnProfitLabel')}
      >
        <CurrencyValue color="green" prefix="+" value={stopOnProfitValue} />
      </LabelValue>
      <LabelValue
        dataTestId="stop-on-loss"
        label={t('autobet.stopOnLossLabel')}
      >
        <CurrencyValue color="red" prefix="-" value={stopOnLossValue} />
      </LabelValue>
    </SimpleGrid>
  );
});

export type AutoBetConfigurationCardProps = CardProps;

// Main component that uses all the smaller components
export const AutoBetConfigurationCard = observer(
  (props: AutoBetConfigurationCardProps) => {
    return (
      <Card
        bd="2px solid dark.6"
        bg="transparent"
        data-test-id="auto-bet-configuration-card"
        p={8}
        withBorder
        {...props}
      >
        <Stack data-test-id="auto-bet-configuration-content" gap="xs">
          <WinLossSection />
          <Divider data-test-id="configuration-divider" size={2} />
          <StopConditionsSection />
        </Stack>
      </Card>
    );
  }
);
